import { cloneElement, useCallback, useEffect, useId, useRef, useState, type ReactElement, type ReactNode } from "react";

/** Load async data; errors are kept and shown, never swallowed. */
export function useLoad<T>(load: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);
  const reload = useCallback(async () => {
    const n = ++seq.current;
    try {
      const value = await load();
      if (n === seq.current) { setData(value); setError(null); }
    } catch (e) {
      if (n === seq.current) setError(errorText(e));
    }
  }, deps);
  useEffect(() => { void reload(); }, [reload]);
  // Three founders share one database: re-read when the tab comes back into
  // view, so a change someone else made shows up without a manual refresh.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") void reload(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [reload]);
  return { data, error, reload, setData };
}

/** Run a write; returns an error message to show, or null on success. */
export function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = useCallback(async (fn: () => Promise<unknown>): Promise<boolean> => {
    setBusy(true); setError(null);
    try { await fn(); return true; }
    catch (e) { setError(errorText(e)); return false; }
    finally { setBusy(false); }
  }, []);
  return { busy, error, run, clear: () => setError(null) };
}

export function errorText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export function ErrorNote({ error }: { error: string | null | undefined }) {
  if (!error) return null;
  return <p className="error" role="alert">{error}</p>;
}

export function Loading() {
  return <p className="muted" aria-busy="true">Loading…</p>;
}

export function PageHeader({ title, sub, children }: { title: ReactNode; sub?: ReactNode; children?: ReactNode }) {
  return (
    <header className="page-header">
      <div>
        <h1>{title}</h1>
        {sub ? <p className="muted">{sub}</p> : null}
      </div>
      {children ? <div className="actions">{children}</div> : null}
    </header>
  );
}

/**
 * A labelled control. The label points at the control by id (not by wrapping
 * it), so the control's accessible name is exactly the label text — a wrapped
 * <select> otherwise gets its option text appended to its name.
 */
export function Field({ label, children }: { label: string; children: ReactElement<{ id?: string }> }) {
  const id = useId();
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {cloneElement(children, { id })}
    </div>
  );
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}
